import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import { toastr } from "react-redux-toastr";
import ErrorMessage from "../../../components/text/ErrorMessage";
import { Customer, Document } from "../../../core/api/models";
import "react-widgets/dist/css/react-widgets.css";
import documentList from "../../../utils/documents.json";
import DateInput from "../../../components/form-group/date-input";
import FileUploadController from "../../../components/upload/FileUploadController";
import api from "../../../api";
import Countries from "../../../utils/countries.json";
import nationalities from "../../../utils/nationality.json";
import {
  useLead,
  useHasPermission,
  AppPermissions,
  State,
} from "../../../core";
import { Dict } from "../../../core/models";
import DocumentView from "./DocumentView";
import { useSelector } from "react-redux";
import companyDocumentList from "../../../utils/companyDocuments.json";
import { DragDropFile } from "../../../components/upload";

const pdfDocumentType = [
  "registrationLicense",
  "signatoryListForMOI",
  "companyCivilId",
  "companyLegalRepresentative",
];

const Documents: React.FunctionComponent<{
  customerDetails?: Customer;
  updated?: () => void;
  canUpdate?: boolean;
}> = (props) => {
  const leadState = useSelector((state: State) => state.lead);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<string>("");
  const [documentSelected, setDocumentSelected] = useState<Dict>();
  const [savedDocuments, setDocumentSaved] = useState<Dict>();
  const { updateDocumentsDetails } = useLead(api.lead);
  const { control, handleSubmit, setValue, errors, watch } = useForm<Document>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });
  const getDocumentList = () => {
    return props.customerDetails?.isCompanyCustomer
      ? [...documentList, ...companyDocumentList]
      : [...documentList];
  };
  const canUpdateCustomerDocuments =
    (useHasPermission(AppPermissions.UpdateCustomerDocuments) &&
      leadState?.leadDetails?.isEditable) ||
    props.canUpdate;

  const onEdit = (docType: string) => {
    selectDocumentType(docType);
    setIsEdit(true);
  };

  const onSubmit = async (data: any) => {
    let documents =
      props.customerDetails && props.customerDetails.documents
        ? props.customerDetails.documents
        : [];
    if (savedDocuments) {
      documents = documents.filter(
        (x) => x.documentType !== savedDocuments?.documentType
      );
    }

    if (
      pdfDocumentType.find((d) => d === (data.documentType || documentType)) &&
      data.document
    ) {
      documents.push({
        _id: savedDocuments?._id,
        documentType,
        issuedDate: data?.issuedDate,
        expiryDate: data?.expiryDate || null,
        document: data?.document,
      });
      const updateData: Customer = {
        documents,
      };

      updateDocumentsDetails(updateData, props.customerDetails?._id, () => {
        toastr.success("Success", "Document saved successfully");
        setIsEdit(false);

        setDocumentType("");

        setDocumentSaved(undefined);

        setDocumentSelected(undefined);
        props.updated?.();
      });
    } else {
      documents.push({
        _id: savedDocuments?._id,
        documentType,
        issuedCountry: data?.issuedCountry,
        nationality: data?.nationality,
        imageFront: data?.imageFront || null,
        imageBack: data?.imageBack || null,
        issuedDate: data?.issuedDate,
        expiryDate: data?.expiryDate || null,
        licenseType: data?.licenseType,
        paciNumber: data?.paciNumber,
        cardSerialNumber: data?.cardSerialNumber,
        chipSerialNumber: data?.chipSerialNumber,
      });
      const updateData: Customer = {
        documents,
      };
      updateDocumentsDetails(updateData, props.customerDetails?._id, () => {
        toastr.success("Success", "Document saved successfully");
        setIsEdit(false);

        setDocumentType("");

        setDocumentSaved(undefined);

        setDocumentSelected(undefined);
        props.updated?.();
      });
    }
  };

  const selectDocumentType = (type: string) => {
    setDocumentType(type);

    const selectedDocument = getDocumentList().find(
      (x) => x.type.toString() === type.toString()
    );
    const savedDocument =
      props.customerDetails && props.customerDetails?.documents
        ? props.customerDetails.documents?.find((x) => x.documentType === type)
        : undefined;

    setDocumentSaved(savedDocument);

    setDocumentSelected(selectedDocument?.fields);
  };

  const savedDocumentTypes = props.customerDetails?.documents?.map(
    (x) => x.documentType
  );

  function renderValidation(elementName: string) {
    if (elementName === "issuedCountry") {
      return (
        <>
          {errors.issuedCountry && (
            <ErrorMessage>Issued Country is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "nationality") {
      return (
        <>
          {errors.nationality && (
            <ErrorMessage>Nationality is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "imageFront") {
      return (
        <>
          {errors.imageFront && (
            <ErrorMessage>Front image is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "imageBack") {
      return (
        <>
          {errors.nationality && (
            <ErrorMessage>Back image is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "issuedDate") {
      return (
        <>
          {errors.issuedDate && (
            <ErrorMessage>Issued Date is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "expiryDate") {
      return (
        <>
          {errors.expiryDate && (
            <ErrorMessage>Expiry Date is required</ErrorMessage>
          )}
        </>
      );
    }

    if (elementName === "licenseType") {
      return (
        <>
          {errors.licenseType && (
            <ErrorMessage>License Type is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "paciNumber") {
      return (
        <>
          {errors.paciNumber && (
            <ErrorMessage>Paci Number is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "cardSerialNumber") {
      return (
        <>
          {errors.cardSerialNumber && (
            <ErrorMessage>Card Serial Number is required</ErrorMessage>
          )}
        </>
      );
    }
    if (elementName === "chipSerialNumber") {
      return (
        <>
          {errors.paciNumber && (
            <ErrorMessage>Chip Serial Number is required</ErrorMessage>
          )}
        </>
      );
    }

    if (elementName === "document") {
      return (
        <>
          {errors.document && <ErrorMessage>Document is required</ErrorMessage>}
        </>
      );
    }
  }

  const validateImage = (elementName: string) => {
    if (elementName === "imageFront" && errors.imageFront) {
      return "Front image is required";
    }
    if (elementName === "imageBack" && errors.imageBack) {
      return "Back image is required";
    }
  };

  const getFormElement = (elementName: string, elementSchema: Dict) => {
    if (elementSchema.type === "text") {
      return (
        <>
          <Controller
            control={control}
            name={elementName}
            rules={{
              required: elementSchema.required,
              validate: (value) => {
                return !!value.trim();
              },
            }}
            defaultValue={savedDocuments ? savedDocuments[elementName] : ""}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder={elementSchema.label}
                name={name}
                value={value}
                onChange={onChange}
                id={elementName}
              />
            )}
          />
          {renderValidation(elementName)}
        </>
      );
    }

    if (elementSchema.type === "date") {
      return (
        <>
          <Controller
            control={control}
            rules={{ required: elementSchema.required }}
            name={elementName}
            defaultValue={savedDocuments ? savedDocuments[elementName] : ""}
            render={({ onChange, value }) => (
              <DateInput
                default={value}
                placeholder={elementSchema.label}
                onChange={onChange}
              />
            )}
          />
          {renderValidation(elementName)}
        </>
      );
    }

    if (elementSchema.type === "select") {
      const defaultValue = elementName === "issuedCountry" ? "India" : "";
      return (
        <>
          <Controller
            control={control}
            rules={{ required: elementSchema.required }}
            name={elementName}
            defaultValue={
              savedDocuments ? savedDocuments[elementName] : defaultValue
            }
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={
                  elementName === "issuedCountry"
                    ? Countries
                    : elementName === "nationality"
                    ? nationalities
                    : elementSchema.options
                }
                textField={"name"}
                valueField={"name"}
                placeholder={`Select ${elementSchema.label}`}
                onChange={(val) => {
                  onChange(val.name);
                }}
                filter={"contains"}
              />
            )}
          />
          {renderValidation(elementName)}
        </>
      );
    }

    if (elementSchema.type === "image") {
      return (
        <FileUploadController
          label={elementSchema.label}
          control={control}
          setValue={setValue}
          name={elementName}
          value={savedDocuments ? savedDocuments[elementName] : ""}
          required={false}
          error={validateImage(elementName)}
        />
      );
    }

    if (elementSchema.type === "pdf") {
      return (
        <>
          <Controller
            control={control}
            name={elementName}
            rules={{ required: elementSchema.required }}
            defaultValue={
              savedDocuments ? savedDocuments[elementName] : watch(elementName)
            }
            render={({ onChange, value, name }) => (
              <DragDropFile
                value={watch(elementName)}
                name={elementName}
                id={elementName}
                onFileChosen={onChange}
                title={elementSchema.label}
                accept={[".png", ".jpeg", ".jpg", ".pdf"]}
              />
            )}
          />
          {renderValidation(elementName)}
        </>
      );
    }
  };

  const documentListTypes: any = savedDocumentTypes
    ? getDocumentList().filter((x) => !savedDocumentTypes.includes(x.type))
    : getDocumentList();
  return (
    <div className="p-4 accordion__body border-bottom">
      {((documentListTypes && documentListTypes.length > 0) || isEdit) && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            {!isEdit && (
              <div className="form-group font-weight-semibold mb-3">
                <label htmlFor="" className="font-sm text-uppercase">
                  Document Type
                </label>

                <DropdownList
                  value={documentType}
                  data={documentListTypes}
                  defaultValue={documentType}
                  textField={"name"}
                  valueField={"type"}
                  placeholder={"Select Document Types"}
                  onChange={(type: any) => selectDocumentType(type.type)}
                  disabled={!canUpdateCustomerDocuments}
                />
              </div>
            )}

            {documentType && documentSelected && (
              <>
                {Object.keys(documentSelected).map((key) => (
                  <div
                    key={key}
                    className="form-group font-weight-semibold mb-3"
                  >
                    {getFormElement(key, documentSelected[key])}
                  </div>
                ))}
              </>
            )}

            <div className="form-group font-weight-semibold mb-3 text-right pt-3">
              <button
                className="btn btn-primary py-2 font-weight-semibold font-normal px-4"
                disabled={!canUpdateCustomerDocuments || !documentType}
                title={
                  !canUpdateCustomerDocuments
                    ? "No permisssion to update documents"
                    : ""
                }
              >
                Save
              </button>
            </div>
          </div>
        </form>
      )}
      {props.customerDetails?.documents && (
        <DocumentView
          onEdit={(docType) => onEdit(docType)}
          customerDetails={props.customerDetails}
        ></DocumentView>
      )}
    </div>
  );
};

export default Documents;
